import { type JSX } from 'react';

import type { Profile } from '@schema/profile.js';
import type { Tile } from '@schema/tiles.js';

import type { BuilderStore } from '../state.js';

import { SelectField, TextField } from './Field.js';
import { type AppearanceTile, TileAppearance } from './TileGridEditor.js';

/**
 * Schema cap (z.array(Tile).max(12)) — the launcher's Tools modal renders
 * a 4-column action grid; 12 = a clean 3-row max before the dialog scrolls.
 */
const MAX_TOOLS = 12;

const AGENT_ACTIONS = ['reboot', 'clearAppData', 'runScript', 'syncNow'] as const;

/**
 * Tools tile shape — same Tile union the schema accepts, but the
 * editor narrows to app / web / agentAction (the three useful
 * dispatch targets). Spacer + folder are valid in the schema and
 * the renderer handles them, but they don't make much sense inside
 * the 3-up Tools action grid; if a customer wants them we can
 * widen here later.
 */
type ToolTile = Extract<Profile['tools'][number], { type: 'app' | 'web' | 'agentAction' }>;
type ToolTileType = ToolTile['type'];

export interface ToolsEditorProps {
  store: BuilderStore;
}

/**
 * Editor for `profile.tools[]` — the custom entries that appear in the
 * launcher's Tools modal, alongside the three universal built-ins
 * (Device Info / Network Check / Calculator).
 *
 * Gated at runtime on the `customTools` feature flag (Pro+); the editor
 * itself stays usable on Free so customers previewing the live preview
 * can still see what their custom tools would look like once they
 * upgrade. The on-device runtime is the enforcement point.
 *
 * Patterned on FeaturedTilesEditor — same shape, same UX, same
 * reuse of TileAppearance for icons + show-label.
 */
export function ToolsEditor({ store }: ToolsEditorProps): JSX.Element {
  const profile = store.activeProfile;
  const tools = profile.tools as ToolTile[];
  const atCapacity = tools.length >= MAX_TOOLS;

  function update(next: ToolTile[]): void {
    store.patchProfile({ tools: next });
  }

  function addTool(): void {
    if (atCapacity) return;
    const next: ToolTile = {
      type: 'web',
      id: crypto.randomUUID(),
      label: `Tool ${String(tools.length + 1)}`,
      url: 'https://example.com',
      openExternal: false,
    };
    update([...tools, next]);
  }

  function removeTool(id: string): void {
    update(tools.filter((t) => t.id !== id));
  }

  function replaceTool(id: string, next: ToolTile): void {
    update(tools.map((t) => (t.id === id ? next : t)));
  }

  function changeType(id: string, nextType: ToolTileType): void {
    const existing = tools.find((t) => t.id === id);
    if (!existing) return;
    // Carry label across; reset type-specific fields to a sensible
    // default. Same pattern as FeaturedTilesEditor.
    if (nextType === 'app') {
      replaceTool(id, {
        type: 'app',
        id,
        label: existing.label,
        packageName: 'com.example.tool',
      });
    } else if (nextType === 'web') {
      replaceTool(id, {
        type: 'web',
        id,
        label: existing.label,
        url: 'https://example.com',
        openExternal: false,
      });
    } else {
      replaceTool(id, { type: 'agentAction', id, label: existing.label, action: 'syncNow' });
    }
  }

  return (
    <div className="editor">
      <header className="editor__head">
        <h2 className="editor__title">Tools</h2>
        <p className="editor__sub">
          Custom entries in the launcher’s Tools modal (the dropdown under the
          header’s grid icon). Built-in tools always render above your entries.
          Up to {String(MAX_TOOLS)} tools; the action grid is 4 columns. Pro+
          feature.{' '}
          <a href="https://mobilauncher.com/docs/customize#tools" target="_blank" rel="noopener noreferrer" className="editor__doc-link">
            Read the docs →
          </a>
        </p>
      </header>

      {tools.length === 0 && (
        <p className="editor__empty">
          No custom tools yet — add up to {String(MAX_TOOLS)}.
        </p>
      )}

      <ol className="featured-list">
        {tools.map((tile, index) => (
          <li key={tile.id} className="featured-list__item">
            <header className="featured-list__head">
              <span className="featured-list__badge">{index + 1}</span>
              <button
                type="button"
                className="featured-list__remove"
                onClick={() => removeTool(tile.id)}
                aria-label={`Remove tool ${String(index + 1)}`}
              >
                Remove
              </button>
            </header>

            <SelectField<ToolTileType>
              label="Type"
              value={tile.type}
              options={[
                { value: 'app', label: 'App' },
                { value: 'web', label: 'Web' },
                { value: 'agentAction', label: 'Agent action' },
              ]}
              onChange={(next) => changeType(tile.id, next)}
            />
            <TextField
              label="Label"
              value={tile.label}
              onChange={(v) => replaceTool(tile.id, { ...tile, label: v })}
            />
            {tile.type === 'app' && (
              <TextField
                label="Package name"
                value={tile.packageName}
                onChange={(v) => replaceTool(tile.id, { ...tile, packageName: v })}
                hint="Android app package name, e.g., com.android.chrome. Find it in Play Store URLs or via `pm list packages` on the device."
              />
            )}
            {tile.type === 'web' && (
              <TextField
                label="URL"
                value={tile.url}
                onChange={(v) => replaceTool(tile.id, { ...tile, url: v })}
                hint="https://…"
              />
            )}
            {tile.type === 'agentAction' && (
              <SelectField<typeof AGENT_ACTIONS[number]>
                label="Action"
                value={tile.action}
                options={AGENT_ACTIONS.map((a) => ({ value: a, label: a }))}
                onChange={(next) => replaceTool(tile.id, { ...tile, action: next })}
              />
            )}

            {/*
              Reuse the icon-source + show-label control from the main
              tile-grid editor. ToolTile is a subset of Tile so the cast
              is sound — TileAppearance only touches fields on TileBase.
            */}
            <TileAppearance
              tile={tile as AppearanceTile}
              onChange={(next) => replaceTool(tile.id, next as ToolTile)}
            />
          </li>
        ))}
      </ol>

      <button
        type="button"
        className="featured-list__add"
        onClick={addTool}
        disabled={atCapacity}
        data-testid="tools-add"
      >
        {atCapacity ? `Maximum (${String(MAX_TOOLS)}) reached` : '+ Add tool'}
      </button>
    </div>
  );
}
